import { useState, useEffect } from 'react'
import { MinimalLayout } from '@/components/layouts/MinimalLayout'
import { EditorNavbar } from '@/components/nav/EditorNavbar'
import { WebWorkerDebugger } from '@/components/core/WebWorkerDebugger'
import { ViewWorkerStatus } from '@/components/core/ViewWorkerStatus'
import { ReadOnlyBanner } from '@/components/banners/ReadOnlyBanner'
import { CadPageContainer } from '@/components/layouts/CadPageContainer'
import {
  PixelBasedResizablePanel,
  PixelBasedResizableContainer,
} from '@/components/ui/pixel-based-resizable'

import { CADEditor } from './components/CADEditor/CADEditor'
import { AssemblyTree } from './components/AssemblyTree'
import { SidePanel } from './components/SidePanel'
import { DraggableTreeNodeContext } from './components/AssemblyTree/DraggableTreeNodeContext'
import { useCADPageListeners } from './hooks/useCADPageListeners'
import { useInitialViewJobs } from './hooks/useInitialViewJobs'
import { useCustomPanelSize } from './hooks/useCustomPanelSize'
import { usePreloadImages } from '@/utils/hooks/usePreloadImages'
import { useAppStore, useCADPageStore } from '@/state'
import { useCADVersionProcessing } from '@/services/hooks/cad_versions'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { ToggleAssemblyTreeButton } from './components/ToggleAssemblyTreeButton'
import { CADVersionProcessingHelper } from './components/CadVersionProcessingHelper'
import { Spinner } from '@/components/ui/spinner'

export const CADPage = () => {
  usePreloadImages()
  useCADPageListeners()
  const [showWebWorkerDebugger, setShowWebWorkerDebugger] =
    useState<boolean>(false)
  const [showViewWorkerStatus, setShowViewWorkerStatus] =
    useState<boolean>(false)
  const toggleStatsPanel = useAppStore((state) => state.toggleStatsPanel)

  useEffect(() => {
    // @ts-expect-error - adding react-query devtools toggle
    window.toggleWebWorkerDebugger = () =>
      setShowWebWorkerDebugger((old) => !old)
    // @ts-expect-error - adding view worker status toggle
    window.toggleViewWorkerStatus = () => setShowViewWorkerStatus((old) => !old)
    // @ts-expect-error - add stats panel
    window.toggleStatsPanel = () => toggleStatsPanel()
  }, [])

  const isAssemblyTreeOpen = useCADPageStore(
    (state) => state.isAssemblyTreeOpen,
  )

  const assemblyPanel = useCustomPanelSize({
    panelId: 'assembly-tree',
    defaultWidthPx: 405,
  })

  const sidePanel = useCustomPanelSize({
    panelId: 'side-panel',
    defaultWidthPx: 320,
  })

  const {
    isLoading,
    isComplete,
    isProcessing,
    data: cadVersionProcess,
  } = useCADVersionProcessing({})

  useInitialViewJobs({ isComplete })

  const { data: docData } = useDocumentPageQuery()
  const documentName = docData.doc?.name

  return (
    <MinimalLayout
      title={`${documentName ? `${documentName} -` : ''} Quarter20 CAD`}
    >
      <EditorNavbar active="cad" />
      <ReadOnlyBanner />
      <CadPageContainer>
        {isLoading && <Spinner />}
        {isComplete && (
          <DraggableTreeNodeContext>
            <PixelBasedResizableContainer className="py-4 px-2">
              <PixelBasedResizablePanel
                width={isAssemblyTreeOpen ? assemblyPanel.width : 30}
                minWidth={isAssemblyTreeOpen ? 320 : 30}
                maxWidth={isAssemblyTreeOpen ? undefined : 30}
                className="pr-2"
                enable={{ right: true }}
                withHandle={isAssemblyTreeOpen}
                onResizeStop={assemblyPanel.handleResize}
              >
                {isAssemblyTreeOpen ? (
                  <AssemblyTree />
                ) : (
                  <div className="mt-6">
                    <ToggleAssemblyTreeButton />
                  </div>
                )}
              </PixelBasedResizablePanel>
              <PixelBasedResizablePanel
                width={sidePanel.width}
                minWidth={340}
                className="pl-2 pr-4 pt-2"
                enable={{ right: true }}
                withHandle
                onResizeStop={sidePanel.handleResize}
              >
                <SidePanel />
              </PixelBasedResizablePanel>
              <div className="flex-1 flex flex-col pt-2">
                <CADEditor />
                {showWebWorkerDebugger && <WebWorkerDebugger />}
                {showViewWorkerStatus && <ViewWorkerStatus />}
              </div>
            </PixelBasedResizableContainer>
          </DraggableTreeNodeContext>
        )}
        {isProcessing && (
          <CADVersionProcessingHelper
            cadVersionId={cadVersionProcess.cadVersionId}
            status={cadVersionProcess.processingStatus}
            hasNoDownloadUrl={cadVersionProcess.hasNoDownloadUrl}
          />
        )}
      </CadPageContainer>
    </MinimalLayout>
  )
}
